import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { ArtReproduction, ArtistProfileView } from '../types';
import ArtworkCard from '../components/gallery/ArtworkCard';
import ArtworkModal from '../components/gallery/ArtworkModal';
import ArtistTierBadge from '../components/ArtistTierBadge';
import { useToast } from '../contexts/ToastContext';
import './ArtistProfile.css';

const ArtistProfile: React.FC = () => {
  const { artistId } = useParams();
  const [profile, setProfile] = useState<ArtistProfileView | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSizes, setSelectedSizes] = useState<Record<number, { size: string; price: number; isOriginal?: boolean }>>({});
  const [selectedArtwork, setSelectedArtwork] = useState<ArtReproduction | null>(null);
  const [showArtworkModal, setShowArtworkModal] = useState<boolean>(false);
  const [addingToCart, setAddingToCart] = useState<boolean>(false);
  const { success, error: showError, warning } = useToast();

  useEffect(() => {
    const fetchProfile = async (): Promise<void> => {
      try {
        const response = await api.get<ArtistProfileView>(`/artists/${artistId}`);
        setProfile(response.data);
      } catch (error) {
        console.error('Failed to fetch artist profile', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [artistId]);

  const handleSizeSelect = (artworkId: number, sizePrice: { size: string; price: number; isOriginal?: boolean }): void => {
    setSelectedSizes(prev => ({
      ...prev,
      [artworkId]: sizePrice
    }));
  };

  const handleAddToCart = async (artwork: ArtReproduction): Promise<void> => {
    try {
      if (addingToCart) return;
      const selectedSize = selectedSizes[artwork.id];
      if (!selectedSize && artwork.sizePrices && artwork.sizePrices.length > 0) {
        warning('Please select a size.');
        return;
      }

      const price = selectedSize?.price || artwork.price;
      const size = selectedSize?.size || 'Standard';
      const isOriginal = selectedSize?.isOriginal || false;
      if (isOriginal && !artwork.isOriginalAvailable) {
        showError('This original artwork is no longer available.');
        return;
      }

      setAddingToCart(true);
      const productData = {
        name: `${artwork.title}${size !== 'Standard' ? ` - ${size}` : ''}`,
        description: artwork.description || `Art by ${artwork.artistName || 'Unknown Artist'}`,
        price: price,
        imageUrl: artwork.imageUrl,
        size: size,
        isCustomPrint: false,
        artReproductionId: artwork.id,
        isOriginal: isOriginal,
      };

      const productResponse = await api.post<{ id: number }>('/products', productData);
      await api.post('/cart', { productId: productResponse.data.id, quantity: 1 });
      success('Added to cart!');
    } catch (error) {
      console.error('Failed to add to cart:', error);
      showError('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleOpenArtwork = (artwork: ArtReproduction): void => {
    setSelectedArtwork(artwork);
    setShowArtworkModal(true);
  };

  if (loading) {
    return <div className="loading">Loading artist profile...</div>;
  }

  if (!profile) {
    return <div className="error">Artist not found.</div>;
  }

  return (
    <div className="artist-profile-page">
      <div className="container">
        <h1>
          {profile.displayName}
          <ArtistTierBadge tier={profile.tier} size="medium" />
        </h1>
        {profile.bio && <p className="artist-bio">{profile.bio}</p>}
        <div className="artist-links">
          {profile.instagram && <a href={`https://instagram.com/${profile.instagram.replace('@', '')}`} target="_blank" rel="noreferrer">Instagram</a>}
          {profile.website && <a href={profile.website} target="_blank" rel="noreferrer">Website</a>}
          {profile.otherSocial && <span>{profile.otherSocial}</span>}
        </div>

        {profile.featuredArtworks.length > 0 && (
          <div className="artist-section">
            <h2>Featured Works</h2>
            <div className="artworks-grid">
              {profile.featuredArtworks.map((artwork) => (
                <ArtworkCard
                  key={artwork.id}
                  artwork={artwork}
                  onOpen={handleOpenArtwork}
                />
              ))}
            </div>
          </div>
        )}

        <div className="artist-section">
          <h2>All Works</h2>
          <div className="artworks-grid">
            {profile.artworks.map((artwork) => (
              <ArtworkCard
                key={artwork.id}
                artwork={artwork}
                onOpen={handleOpenArtwork}
              />
            ))}
          </div>
        </div>
      </div>
      <ArtworkModal
        isOpen={showArtworkModal}
        artwork={selectedArtwork}
        selectedSize={selectedArtwork ? selectedSizes[selectedArtwork.id] : undefined}
        onSizeSelect={handleSizeSelect}
        onAddToCart={handleAddToCart}
        isSubmitting={addingToCart}
        onClose={() => {
          setShowArtworkModal(false);
          setSelectedArtwork(null);
        }}
      />
    </div>
  );
};

export default ArtistProfile;
